/**
 * VRG-455 — record where a package of tags physically went.
 *
 *   PATCH /packages/:id/consignment-destination
 *
 * This is a SECURITY control, not a metadata field. `retail_or_held` stops a
 * buyer's scan claiming the certificates for tags in that box, which is what
 * makes shop-floor stock unclaimable; `unknown` (the default every existing
 * package sits on) leaves them claimable.
 *
 * Gated on the same permission as binding a package to a product
 * (`certificate_create_from_package`) — the same actor deciding about the same
 * box on the same surface.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createApiCall } from 'services'

import { tagOrdersKeys } from './useTagOrders'

export type ConsignmentDestination =
  | 'retail_or_held'
  | 'direct_to_consumer'
  | 'unknown'

type SetConsignmentDestinationParams = {
  id: string
  destination: ConsignmentDestination
}

export const useSetConsignmentDestination = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['packages', 'consignment-destination'],
    mutationFn: async ({ id, destination }: SetConsignmentDestinationParams) => {
      const response = await createApiCall({
        method: 'PATCH',
        url: `/packages/${id}/consignment-destination`,
        data: { destination },
      })
      // `createApiCall` RESOLVES on 4xx/5xx rather than rejecting (see
      // `useUpdatePackage`), so a 403 would otherwise read as a saved setting —
      // and this particular setting silently not saving is the failure the
      // whole control exists to prevent.
      const status = response?.status
      if (typeof status === 'number' && (status < 200 || status >= 300)) {
        const error: Error & { status?: number } = new Error(
          (response?.data as any)?.message ||
            'Could not record where this package went',
        )
        error.status = status
        throw error
      }
      return response.data as {
        success: boolean
        consignment_destination: ConsignmentDestination
      }
    },
    onSuccess: () => {
      // Both lists carry the value: the internal packages table and the brand's
      // own order page.
      queryClient.invalidateQueries({ queryKey: ['packages'] })
      // The brand's order page reads its boxes under the tag-order detail key
      // (`useTagOrderPackages`), not under `['packages']` — that is the internal
      // list's key, and invalidating only it would leave the surface the brand
      // actually set this from showing the old value.
      queryClient.invalidateQueries({ queryKey: tagOrdersKeys.all })
    },
  })
}
